import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import axios from 'axios'

const SWIPE_THRESHOLD = 0.15
const GESTURE_CLAIM_THRESHOLD = 8

export function useVerticalReelSwipe({
    videos,
    currentIndex,
    routePrefix,
    // { type: 'performer'|'fantasy'|'tag'|'member'|'members'|'scene', slug, label } on a filtered feed, null otherwise.
    filter = null,
    // On the SFW feed every URL this engine builds has to stay inside the SFW feed.
    isSfwFeed = false,
    initialClipSlug,
    perPage,
    nextPage,
    // Ids of the clips the current member has already liked, for the clips in the
    // initial payload only. Every `.next` page brings its own set, so this state has
    // to live in here alongside `videos` rather than being seeded once by the page.
    likedClipIds = [],
    swipeLimit,
    memberHasUnlimitedAccess,
    timeoutLimitReached,
}) {
    const reelViewportRef = ref(null)
    const videoContainerRef = ref(null)
    // v-for template refs, keyed by clip id. shallowRef + replace on write: a mutated Map
    // would not invalidate the computed below.
    const playerRefs = shallowRef(new Map())
    const slideHeight = ref(600)
    const dragOffset = ref(0)
    const isDragging = ref(false)
    const gestureClaimed = ref(false)
    const isAnimating = ref(false)
    const touchStartY = ref(0)
    const touchStartX = ref(0)
    const lastTouchY = ref(0)
    const lastTouchTime = ref(0)
    const isMobileDevice = ref(false)
    const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
    const windowHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 768)
    const countedVideoIds = ref([])
    // Replaced on every write, never mutated in place: Vue does not track adds/deletes
    // inside a plain Set held by a ref, so a mutation would leave the heart stale.
    const likedIds = ref(new Set(likedClipIds ?? []))
    let nextLoading = false
    let wheelTimeout = null
    let keyTimeout = null
    // The swipe animation's commit. Cleared on unmount: firing after an Inertia visit would
    // commitTo*Video() and replaceState this feed's URL over the page just navigated to.
    let transitionTimeout = null
    let resizeObserver = null
    let unsubscribeInitialNavigate = null

    const setPlayerRef = (id, el) => {
        if (id === undefined || id === null) {
            return
        }

        if (el) {
            if (playerRefs.value.get(id) === el) {
                return
            }

            playerRefs.value = new Map(playerRefs.value).set(id, el)

            return
        }

        if (!playerRefs.value.has(id)) {
            return
        }

        const next = new Map(playerRefs.value)
        next.delete(id)
        playerRefs.value = next
    }

    const currentVideoData = computed(() => videos.value[currentIndex.value] ?? null)

    const currentVideoPlayerRef = computed(
        () => playerRefs.value.get(videos.value[currentIndex.value]?.id) ?? null
    )

    const nextVideoData = computed(() => {
        if (currentIndex.value >= videos.value.length - 1) {
            return null
        }

        return videos.value[currentIndex.value + 1]
    })

    const previousVideoData = computed(() => {
        if (currentIndex.value === 0) {
            return null
        }

        return videos.value[currentIndex.value - 1]
    })

    // Once the free swipe limit is hit every clip locks — there is no partial-lock mode.
    const shouldVideoBeLocked = computed(() => () => {
        if (memberHasUnlimitedAccess.value) {
            return false
        }

        return timeoutLimitReached.value
    })

    // Hard gate: every navigation path (arrows, wheel, keys, swipe commit) funnels
    // through showNextVideo/showPreviousVideo, so blocking there closes all of them.
    const isNavigationBlocked = () => !memberHasUnlimitedAccess.value && timeoutLimitReached.value

    const shouldAutoplay = (index) => {
        return !shouldVideoBeLocked.value(index) && index === currentIndex.value
    }

    const windowStart = computed(() => Math.max(0, currentIndex.value - 1))

    // Mounted window: one slide behind the current one, one ahead — prev/current/next, the
    // same three slots FeedV2.vue mounts. Only the current slide preloads media; the two
    // neighbours fetch nothing through `preload`. `metadata` still pulled each neighbour's moov
    // atom — 540-923 KB apiece on the BTS clips, ~1.8 MB of pure index competing with the
    // visible slide's download. Their posters cover the first frame, so nothing regresses
    // visually. The one exception is `isNext`: once the current clip plays, the next slide may
    // prefetch exactly one HLS fragment so the swipe starts from buffer (VideoPlayer.vue, NEX-9360).
    const windowedSlides = computed(() => {
        const end = Math.min(videos.value.length - 1, currentIndex.value + 1)
        const slides = []

        for (let index = windowStart.value; index <= end; index++) {
            const video = videos.value[index]

            if (!video) {
                continue
            }

            slides.push({
                video,
                index,
                isCurrent: index === currentIndex.value,
                isNext: index === currentIndex.value + 1,
                preloadMode: index === currentIndex.value ? 'auto' : 'none',
            })
        }

        return slides
    })

    const reelTrackStyle = computed(() => {
        // Offset by the current slide's position inside the window, not a fixed one slide:
        // at the head of the feed there is no slide above the current one.
        const base = -(currentIndex.value - windowStart.value) * slideHeight.value
        const offset = dragOffset.value
        const transition = isAnimating.value
            ? 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            : 'none'

        return {
            transform: `translateY(calc(${base}px + ${offset}px))`,
            transition,
        }
    })

    const reelViewportStyle = computed(() => {
        if (typeof window === 'undefined') {
            return { width: '100%', height: '100vh', margin: '0 auto' }
        }

        if (windowWidth.value >= 1024) {
            return { width: '560px', height: '100dvh', margin: '0 auto' }
        }

        return { width: '100%', height: `${windowHeight.value}px`, margin: '0 auto' }
    })

    const updateSlideHeight = () => {
        if (reelViewportRef.value) {
            slideHeight.value = reelViewportRef.value.clientHeight
        }
    }

    const handleWindowResize = () => {
        windowWidth.value = window.innerWidth
        windowHeight.value = window.innerHeight
        updateSlideHeight()
    }

    const checkMobileDevice = () => {
        const userAgent = window.navigator.userAgent
        const mobileKeywords = ['Android', 'webOS', 'iPhone', 'iPad', 'iPod', 'BlackBerry', 'Windows Phone']

        isMobileDevice.value = mobileKeywords.some((keyword) => userAgent.includes(keyword))
    }

    const getPointerY = (e) => (e.touches ? e.touches[0].clientY : e.clientY)
    const getPointerX = (e) => (e.touches ? e.touches[0].clientX : e.clientX)

    const bumpSwipeCount = () => {
        if (memberHasUnlimitedAccess.value) {
            return
        }

        if (countedVideoIds.value.length >= swipeLimit.value) {
            timeoutLimitReached.value = true
        }

        axios.post(route(routePrefix + '.verticalClips.timeout')).then((response) => {
            timeoutLimitReached.value = response.data.limit_reached
        })
    }

    const recordSwipeForCurrentVideo = () => {
        if (memberHasUnlimitedAccess.value || swipeLimit.value <= 0) {
            return
        }

        const video = videos.value[currentIndex.value]

        if (!video || countedVideoIds.value.includes(video.id)) {
            return
        }

        bumpSwipeCount()
        countedVideoIds.value = [...countedVideoIds.value, video.id]
    }

    // The sfw route family mirrors the normal one name for name, so every URL built here
    // stays on whichever feed the visitor is actually on.
    const feedRouteName = (prefix, suffix = '') =>
        prefix + '.verticalClips.' + (isSfwFeed ? 'sfw.' : '') + 'feed' + suffix

    // Tag feeds have no sfw counterpart — there is no sfw.feed.tag route — so their name is
    // built without the segment feedRouteName would insert. isSfwFeed is route-derived, so a
    // tag feed is never SFW to begin with; this just makes an unroutable name unconstructable.
    const tagFeedRouteName = (prefix) => prefix + '.verticalClips.feed.tag'

    // Same for scene feeds: there is no sfw.feed.scene route either.
    const sceneFeedRouteName = (prefix) => prefix + '.verticalClips.feed.scene'

    // A filtered feed rewrites to its own route, so a swipe never silently drops the
    // performer, fantasy, tag, member or scene the visitor arrived on.
    const feedUrl = (clipSlug) => {
        if (filter?.type === 'performer') {
            return route(feedRouteName(routePrefix, '.performer'), {
                performer: filter.slug,
                clip: clipSlug,
            })
        }

        if (filter?.type === 'fantasy') {
            return route(feedRouteName(routePrefix, '.fantasy'), {
                fantasy: filter.slug,
                clip: clipSlug,
            })
        }

        if (filter?.type === 'tag') {
            return route(tagFeedRouteName(routePrefix), {
                tag: filter.slug,
                clip: clipSlug,
            })
        }

        if (filter?.type === 'scene') {
            return route(sceneFeedRouteName(routePrefix), {
                scene: filter.slug,
                clip: clipSlug,
            })
        }

        if (filter?.type === 'member') {
            return route(feedRouteName(routePrefix, '.member'), {
                member: filter.slug,
                clip: clipSlug,
            })
        }

        // Every member clip. Members only, so publicClipUrl() below has no branch for it
        // and shares the plain tour clip URL.
        if (filter?.type === 'members') {
            return route(routePrefix + '.verticalClips.feed.members', { clip: clipSlug })
        }

        return route(feedRouteName(routePrefix), { clip: clipSlug })
    }

    // Clips are publicly visible, so anything handed to a visitor to share (the copy
    // link, socials) must resolve on the public tour domain — always 'www', never the
    // current page's own routePrefix, which on members is the members-only domain.
    const publicClipUrl = (clipSlug) => {
        if (filter?.type === 'performer') {
            return route(feedRouteName('www', '.performer'), {
                performer: filter.slug,
                clip: clipSlug,
            })
        }

        if (filter?.type === 'fantasy') {
            return route(feedRouteName('www', '.fantasy'), {
                fantasy: filter.slug,
                clip: clipSlug,
            })
        }

        if (filter?.type === 'tag') {
            return route(tagFeedRouteName('www'), {
                tag: filter.slug,
                clip: clipSlug,
            })
        }

        if (filter?.type === 'scene') {
            return route(sceneFeedRouteName('www'), {
                scene: filter.slug,
                clip: clipSlug,
            })
        }

        if (filter?.type === 'member') {
            return route(feedRouteName('www', '.member'), {
                member: filter.slug,
                clip: clipSlug,
            })
        }

        return route(feedRouteName('www'), { clip: clipSlug })
    }

    // Rebuilt from Ziggy rather than by string surgery on the current path — the feed is
    // also reachable at /clips with no slug segment to replace.
    const updateBrowserUrl = (videoData) => {
        if (!videoData?.slug) {
            return
        }

        // The feed route carries no query params of its own, so whatever is on the current
        // URL (?videos=0) is ours to preserve across swipes.
        window.history.replaceState({}, '', feedUrl(videoData.slug) + window.location.search)
    }

    const nextRoute = () => {
        const video = videos.value[currentIndex.value]

        return route(feedRouteName(routePrefix, '.next'), {
            clip: video?.slug ?? initialClipSlug,
        })
    }

    // The pagination endpoint is shared between filtered and unfiltered feeds; the filter
    // rides in the body. Both fields absent means the unfiltered feed, as before.
    const nextPayload = () => {
        if (!filter?.type || !filter?.slug) {
            return {}
        }

        return { filter_type: filter.type, filter_slug: filter.slug }
    }

    const isClipLiked = (clipId) => likedIds.value.has(clipId)

    const setClipLiked = (clipId, liked) => {
        if (clipId === undefined || clipId === null) {
            return
        }

        const next = new Set(likedIds.value)

        if (liked) {
            next.add(clipId)
        } else {
            next.delete(clipId)
        }

        likedIds.value = next
    }

    // Merged, never assigned: the pagination response only carries liked state for the
    // clips in that page, so overwriting would unfill everything already on screen.
    const mergeLikedIds = (clipIds) => {
        if (!clipIds?.length) {
            return
        }

        likedIds.value = new Set([...likedIds.value, ...clipIds])
    }

    const loadMoreVideos = async (videoIndex) => {
        if (nextLoading || !nextPage.value) {
            return
        }

        const videosLeft = videos.value.length - videoIndex - 1

        if (videosLeft > 2) {
            return
        }

        nextLoading = true

        try {
            const response = await axios.post(nextRoute(), nextPayload())

            nextPage.value = response.data?.next_page ?? null

            // Liked state rides alongside the (cached) clip payload rather than inside
            // it, so it arrives with each page and has to be folded in here.
            mergeLikedIds(response.data?.likedClipIds)

            if (response.data?.data?.length) {
                const existingIds = new Set(videos.value.map((v) => v.id))
                const newClips = response.data.data.filter((clip) => !existingIds.has(clip.id))

                if (newClips.length) {
                    videos.value = [...videos.value, ...newClips]
                }
            }
        } finally {
            nextLoading = false
        }
    }

    const commitToNextVideo = () => {
        if (!nextVideoData.value) {
            loadMoreVideos(currentIndex.value)

            return
        }

        updateBrowserUrl(nextVideoData.value)
        recordSwipeForCurrentVideo()
        currentIndex.value++
    }

    const commitToPreviousVideo = () => {
        if (!previousVideoData.value) {
            return
        }

        updateBrowserUrl(previousVideoData.value)
        recordSwipeForCurrentVideo()
        currentIndex.value--
    }

    const showNextVideo = () => {
        // Past the limit, navigation does nothing at all — only the lock card links out.
        if (isNavigationBlocked()) {
            return
        }

        if (!nextVideoData.value || isAnimating.value) {
            return
        }

        isAnimating.value = true
        dragOffset.value = -slideHeight.value
        transitionTimeout = setTimeout(() => {
            commitToNextVideo()
            dragOffset.value = 0
            isAnimating.value = false
        }, 250)
    }

    const showPreviousVideo = () => {
        if (isNavigationBlocked()) {
            return
        }

        if (!previousVideoData.value || isAnimating.value) {
            return
        }

        isAnimating.value = true
        dragOffset.value = slideHeight.value
        transitionTimeout = setTimeout(() => {
            commitToPreviousVideo()
            dragOffset.value = 0
            isAnimating.value = false
        }, 250)
    }

    const onReelSwipeStart = (e) => {
        if (e.target.tagName === 'BUTTON' || e.target.closest('button') || e.target.closest('a')) {
            return
        }

        if (isAnimating.value) {
            return
        }

        isDragging.value = true
        gestureClaimed.value = false
        touchStartY.value = getPointerY(e)
        touchStartX.value = getPointerX(e)
        lastTouchY.value = touchStartY.value
        lastTouchTime.value = Date.now()
    }

    const onReelSwipeMove = (e) => {
        if (!isDragging.value) {
            return
        }

        e.preventDefault()

        const y = getPointerY(e)
        const x = getPointerX(e)
        const verticalDelta = Math.abs(y - touchStartY.value)
        const horizontalDelta = Math.abs(x - touchStartX.value)

        if (!gestureClaimed.value && verticalDelta >= GESTURE_CLAIM_THRESHOLD && verticalDelta > horizontalDelta) {
            gestureClaimed.value = true
        }

        // No half-drag snapback once the limit is reached.
        if (gestureClaimed.value && !isMobileDevice.value && !isNavigationBlocked()) {
            const delta = touchStartY.value - y
            const maxDrag = slideHeight.value * 0.5
            dragOffset.value = Math.max(-maxDrag, Math.min(maxDrag, delta))
        }

        lastTouchY.value = y
        lastTouchTime.value = Date.now()
    }

    const onReelSwipeEnd = (e) => {
        if (!isDragging.value) {
            return
        }

        const endY = e.changedTouches?.[0]?.clientY ?? lastTouchY.value
        const delta = touchStartY.value - endY
        const threshold = slideHeight.value * SWIPE_THRESHOLD
        const shouldGoNext = nextVideoData.value && delta > threshold
        const shouldGoPrev = previousVideoData.value && delta < -threshold

        if (gestureClaimed.value) {
            e.preventDefault()
            e.stopPropagation()
        }

        isDragging.value = false

        // Mobile path: a claimed gesture past the limit is swallowed, never a swipe.
        if (gestureClaimed.value && isNavigationBlocked()) {
            gestureClaimed.value = false
            dragOffset.value = 0

            return
        }

        if (shouldGoNext) {
            showNextVideo()
        } else if (shouldGoPrev) {
            showPreviousVideo()
        } else {
            isAnimating.value = true
            dragOffset.value = 0
            transitionTimeout = setTimeout(() => {
                isAnimating.value = false
            }, 200)
        }
    }

    const handleWheelNavigation = (event) => {
        if (isMobileDevice.value) {
            return
        }

        event.preventDefault()

        if (Math.abs(event.deltaY) <= 65 || wheelTimeout) {
            return
        }

        if (event.deltaY > 0 && nextVideoData.value) {
            showNextVideo()
        } else if (event.deltaY < 0 && previousVideoData.value) {
            showPreviousVideo()
        }

        wheelTimeout = setTimeout(() => {
            wheelTimeout = null
        }, 400)
    }

    const handleKeyboardNavigation = (event) => {
        if (!['ArrowUp', 'ArrowDown'].includes(event.key)) {
            return
        }

        event.preventDefault()

        if (keyTimeout) {
            return
        }

        if (event.key === 'ArrowUp' && previousVideoData.value) {
            showPreviousVideo()
        } else if (event.key === 'ArrowDown' && nextVideoData.value) {
            showNextVideo()
        }

        keyTimeout = setTimeout(() => {
            keyTimeout = null
        }, 500)
    }

    watch(currentIndex, (newIndex, oldIndex) => {
        if (oldIndex !== undefined && newIndex !== undefined) {
            loadMoreVideos(newIndex)
        }
    })

    onMounted(() => {
        checkMobileDevice()

        // A slug-less landing (e.g. bare /clips) still resolves to one specific clip
        // server-side, but the address bar keeps showing the index until a swipe calls
        // updateBrowserUrl — so Share, before any swipe, would hand out the index URL
        // instead of this clip's. Align the two up front — but only once Inertia's own
        // initial-load history sync (CurrentPage#set, which runs after this component
        // mounts) has finished: it pushes/replaces state using the original request URL,
        // so calling replaceState any earlier here gets silently overwritten right back.
        // 'navigate' fires once that sync completes, for the first load same as any visit.
        unsubscribeInitialNavigate = router.on('navigate', () => {
            updateBrowserUrl(currentVideoData.value)
            unsubscribeInitialNavigate?.()
            unsubscribeInitialNavigate = null
        })

        if (swipeLimit.value > 0 && !memberHasUnlimitedAccess.value) {
            const video = videos.value[currentIndex.value]

            if (video && !countedVideoIds.value.includes(video.id)) {
                bumpSwipeCount()
                countedVideoIds.value = [...countedVideoIds.value, video.id]
            }
        }

        window.addEventListener('resize', handleWindowResize)
        window.addEventListener('orientationchange', handleWindowResize)

        nextTick(() => {
            if (videoContainerRef.value) {
                videoContainerRef.value.setAttribute('tabindex', '0')
                videoContainerRef.value.addEventListener('keydown', handleKeyboardNavigation)

                if (!isMobileDevice.value) {
                    videoContainerRef.value.addEventListener('wheel', handleWheelNavigation, { passive: false })
                }

                videoContainerRef.value.focus()
            }

            updateSlideHeight()

            if (reelViewportRef.value) {
                resizeObserver = new ResizeObserver(updateSlideHeight)
                resizeObserver.observe(reelViewportRef.value)
                reelViewportRef.value.addEventListener('touchmove', onReelSwipeMove, { passive: false })
            }
        })
    })

    onBeforeUnmount(() => {
        unsubscribeInitialNavigate?.()

        window.removeEventListener('resize', handleWindowResize)
        window.removeEventListener('orientationchange', handleWindowResize)

        if (videoContainerRef.value) {
            videoContainerRef.value.removeEventListener('keydown', handleKeyboardNavigation)
            videoContainerRef.value.removeEventListener('wheel', handleWheelNavigation)
        }

        resizeObserver?.disconnect()
        resizeObserver = null
        reelViewportRef.value?.removeEventListener('touchmove', onReelSwipeMove)

        clearTimeout(transitionTimeout)
        clearTimeout(wheelTimeout)
        clearTimeout(keyTimeout)
    })

    return {
        reelViewportRef,
        videoContainerRef,
        currentVideoPlayerRef,
        currentVideoData,
        nextVideoData,
        previousVideoData,
        windowedSlides,
        setPlayerRef,
        shouldVideoBeLocked,
        shouldAutoplay,
        reelTrackStyle,
        reelViewportStyle,
        onReelSwipeStart,
        onReelSwipeEnd,
        showNextVideo,
        showPreviousVideo,
        publicClipUrl,
        likedIds,
        isClipLiked,
        setClipLiked,
    }
}
